import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { MENU } from "@app/evaluation/menu";
import { MODULES } from "@app/evaluation/modules";
import { CultevaMenu } from "@core/menu";
import { CultevaModules } from "@core/modules";
import { NgxsModule } from "@ngxs/store";
import { AdvancedReportState } from "./components/report-view/report-state/advanced-report.state";
import { ComponentState } from "./components/report-components/report-component.state";
import { ReportView } from "./components/report-view/advanced-report.view";
import { PageState } from "./components/report-page/report-page-state/report-page.state";
import { SharedModule } from "@app/shared.module";
import { AppAuthModule } from "@app/auth";
import { AppMaterialModule } from "@core/material";
import { CoreDataModule } from "@core/data";
import { AppChartsModule } from "@core/charts";
import { CoreUtilsModule } from "@core/utils";
import { AppBrowserModule } from "@core/browser";
import { GridsterModule } from "angular-gridster2";
import { NgxDocViewerModule } from "ngx-doc-viewer2";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { DocumentRef } from "@core/browser/document-ref";
import { FontLoaderService } from "@core/fonts/font-loader.service";
import { EditModeHintService } from "./services/edit-mode-hint.service";
import { ReportImageComponent } from "./components/report-components/report-image-component/report-image-component";
import { ReportTextComponent } from "./components/report-components/report-text-component/report-text-component";
import { ReportChartComponent } from "./components/report-components/report-chart-component/report-chart-component";
import { ReportComponentFormDialog } from "./components/report-component-form/report-component-form.dialog";
import { ReportTextEditFormDialog } from "./components/report-dialogs/report-text-edit-form/report-text-edit-form.dialog";
import { ReportImageEditFormDialog } from "./components/report-dialogs/report-image-edit-form/report-image-edit-form.dialog";
import { ReportImagePreviewDialog } from "./components/report-dialogs/report-image-edit-form/report-image-preview.dialog";
import { ReportComponentFormState } from "./components/report-component-form/report-component-form.state";
import { AdvancedReportIndexView } from "./components/report-index/report-index.view";
import { AdvancedReportIndexState } from "./components/report-index/report-index.state";
import { ReportRoutingModule } from "./advanced-report-routing.module";
import { ReportPageView } from "./components/report-page/report-page.view";
import { ReportComponentView } from "./components/report-components/report-component.view";
import { QuillModule } from "ngx-quill";
import Quill from "quill";
import { FONT_FAMILY_OPTIONS } from "@core/data/types/advanced-report";
import { ReportFilterDialog } from "./components/report-dialogs/report-filter-dialog/report-filter-form.dialog";
import { ReportFilterFormState } from "./components/report-dialogs/report-filter-dialog/report-filter-form.dialog.state";
import { AdvancedReportFormDialog } from "./components/report-dialogs/report-dialog/advanced-report-form.dialog";
import { AdvancedReportFormState } from "./components/report-dialogs/report-dialog/advanced-report-form.state";
import { LIBRARY } from './library';
import { Library } from "@library";
import { ReportImageFormState } from "./components/report-dialogs/report-image-edit-form/report-image-edit-form.state";
import { ReportChartEditFormDialog } from "./components/report-dialogs/report-chart-edit-form/report-chart-edit-form.dialog";
import { ReportChartFormState } from "./components/report-dialogs/report-chart-edit-form/report-chart-edit-form.state";
import { PvPageSettings } from "./components/report-page/report-page-settings/report-page-settings.component";
import { InheritableTypographyControl } from "./components/inheritable-typography/inheritable-typography.control";
import { ReportComponentTypographyDialog } from "./components/report-dialogs/report-component-typography-dialog/report-component-typography.dialog";
import { PvReportSettings } from "./components/report-view/report-settings-component/report-settings.component";
import { AdvancedReportShareDialog } from "./components/report-dialogs/report-share-dialog/advanced-report-share.dialog";
import { AdvancedReportShareDialogState } from "./components/report-dialogs/report-share-dialog/advanced-report-share.dialog.state";
import { AdvancedReportPreview } from "./components/report-preview/advanced-report-preview.view";
import { AdvancedReportPreviewState } from "./components/report-preview/advanced-report-preview.state";
import { ReportTableComponent } from "./components/report-components/report-table-component/report-table-component";
import { ReportTableEditFormDialog } from "./components/report-dialogs/report-table-edit-form/report-table-edit.form.dialog";
import { ReportTableFormState } from "./components/report-dialogs/report-table-edit-form/report-table-edit.form.state";
import { ReportIndexComponent } from './components/report-components/report-index-component/report-index-component';
import { ReportIndexEditFormDialog } from './components/report-dialogs/report-index-edit-form/report-index-edit-form.dialog';
import { ReportIndexFormState } from './components/report-dialogs/report-index-edit-form/report-index-edit-form.state';
import { ReportGanttComponent } from './components/report-components/report-gantt-component/report-gantt-component';
import { ReportGanttGraph } from './components/report-components/report-gantt-component/report-gantt-graph';
import { ReportGanttEditFormDialog } from './components/report-dialogs/report-gantt-edit-form/report-gantt-edit-form.dialog';
import { ReportGanttFormState } from './components/report-dialogs/report-gantt-edit-form/report-gantt-edit-form.state';
import { DescribeIndexDataPoint } from './pipes/index-data-point.pipe';
import { GenericReportGraph } from './components/report-components/generic-graph-component/generic-graph-component';
import { DescribeTableDataPoint } from './pipes/table-data-point.pipe';
import { FilterByCategoryContext } from './pipes/filter-by-category-context.pipe';
import { DescribeTableRowPipe } from './pipes/describe-table-row.pipe';
import { DpReorderListComponent } from './components/report-dialogs/dp-reorder-list/dp-reorder-list.component';

const libraryInstance = new Library;
const menuInstance = new CultevaMenu;
const modulesInstance = new CultevaModules;

// Register the report font families on Quill as a style attributor (inline font-family, so pasted
// fonts survive) and expose them in the editor toolbar's font dropdown.
const QuillFontStyle: any = (Quill as any).import('attributors/style/font');
QuillFontStyle.whitelist = FONT_FAMILY_OPTIONS;
(Quill as any).register(QuillFontStyle, true);

@NgModule({
    imports: [
        CommonModule,
        CoreDataModule,
        AppMaterialModule,
        AppChartsModule,
        AppAuthModule,
        CoreUtilsModule,
        AppBrowserModule,
        NgxMatSelectSearchModule,
        NgxDocViewerModule,
        SharedModule,
        GridsterModule,
        ReportRoutingModule,
        NgxsModule.forFeature([
            //states
            AdvancedReportState,
            PageState,
            ComponentState,
            AdvancedReportIndexState,
            AdvancedReportPreviewState,

            //dialog states
            ReportComponentFormState,
            ReportFilterFormState,
            AdvancedReportFormState,
            ReportImageFormState,
            ReportChartFormState,
            ReportTableFormState,
            ReportIndexFormState,
            ReportGanttFormState,
            AdvancedReportShareDialogState,
        ]),
        QuillModule.forRoot(),

    ],
    declarations: [
        //Views
        AdvancedReportIndexView,
        ReportView,
        ReportPageView,
        ReportComponentView,
        AdvancedReportPreview,

        //Dialogs
        ReportComponentFormDialog,
        ReportTextEditFormDialog,
        ReportImageEditFormDialog,
        ReportImagePreviewDialog,
        ReportChartEditFormDialog,
        ReportTableEditFormDialog,
        ReportIndexEditFormDialog,
        ReportGanttEditFormDialog,
        ReportFilterDialog,
        AdvancedReportFormDialog,
        AdvancedReportShareDialog,

        //Autocomplete

        //Autocomplete chips

        //Lines

        //Pipes
        DescribeIndexDataPoint,
        DescribeTableDataPoint,
        FilterByCategoryContext,
        DescribeTableRowPipe,

        // Components
        ReportImageComponent,
        ReportTextComponent,
        ReportChartComponent,
        ReportIndexComponent,
        ReportTableComponent,
        ReportGanttComponent,
        ReportGanttGraph,
        GenericReportGraph,

        // Utils Components
        PvPageSettings,
        PvReportSettings,
        InheritableTypographyControl,
        ReportComponentTypographyDialog,
        DpReorderListComponent,

    ],
    exports: [
        DpReorderListComponent,
    ],
    providers: [
        {
            provide: LIBRARY,
            useValue: libraryInstance
        },
        {
            provide: MENU,
            useValue: menuInstance
        },
        {
            provide: MODULES,
            useValue: modulesInstance
        },
        DocumentRef,
        FontLoaderService,
        EditModeHintService,
    ],
    entryComponents: [
        //Dialogs
        ReportComponentTypographyDialog,
        ReportComponentFormDialog,
        ReportTextEditFormDialog,
        ReportImageEditFormDialog,
        ReportImagePreviewDialog,
        ReportChartEditFormDialog,
        ReportTableEditFormDialog,
        ReportIndexEditFormDialog,
        ReportGanttEditFormDialog,
        ReportFilterDialog,
        AdvancedReportFormDialog,
        AdvancedReportShareDialog,
    ]
})
export class AdvancedReportModule { }
